import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import worldTournamentCupImage from '../assets/home-icons/world-tournament-cup-matte.png';
import NoteList from './NoteList';
import NoteImportNotice from './NoteImportNotice';
import ErrorMessage from './ErrorMessage';
import ImportButton from '../features/import-note/components/ImportButton';
import PracticeSettings from './PracticeSettings';
import TrainingHero from './TrainingHero';
import HomeLearningPreviews from './HomeLearningPreviews';
import VocabularyHomeEntry from './VocabularyHomeEntry';
import PracticePoolRecommendation from './PracticePoolRecommendation';
import { getPracticePoolWarningLevel } from '../utils/practicePoolWarning';
import databaseService from '../services/DatabaseService';
import navigationService from '../services/NavigationService';
import authService from '../services/AuthService';
import StreakService from '../services/StreakService';
import { STREAK_PROTECTION_CHANGED, getAccountToday } from '../services/StreakProtection';
import PracticeSettingsService from '../services/PracticeSettingsService';
import { buildPracticePlayerPlaylist } from '../services/PracticePlayerService';
import apiClient from '../services/apiClient';
import { unlockPracticeAudio } from '../services/soundEffects';
import TournamentResultPopup from './TournamentResultPopup';
import NotificationPopup from './NotificationPopup';
import NoCardsPracticeScene from './NoCardsPracticeScene';
import InitialManualScene from './InitialManualScene';
import { dismissNotification, isNotificationDismissed } from '../services/NotificationDismissalService';
import { useConfirmDialog } from './ConfirmDialog';
import { getLanguage, t } from '../i18n';
import { getTournamentEntryPlacement, getTournamentSignupCountdown } from '../utils/tournamentEntryPlacement';
import { THEME_SURFACE_COLORS } from '../services/ThemeService';
import usePageThemeColor from '../hooks/usePageThemeColor';
import './MainPage.css';

const tournamentLabelKeys = {
  'Group Stage': 'groupStage', 'Round of 16': 'roundOf16',
  'Quarter-final': 'quarterFinal', 'Semi-final': 'semiFinal', Final: 'final',
  Champion: 'champion', 'Runner-up': 'runnerUp',
  'Semi-finalist': 'semiFinalist', 'Quarter-finalist': 'quarterFinalist',
};

const localizeTournamentLabel = (label) => (
  Object.prototype.hasOwnProperty.call(tournamentLabelKeys, label)
    ? t(`dashboard.tournament.labels.${tournamentLabelKeys[label]}`)
    : label
);

/**
 * Shared home dashboard and notes collection, with independent route lifecycles.
 * 
 * Requirements:
 * - 1.1: Display all notes from the database
 * - 6.3: Display loading indicator during async operations
 * - 6.4: Remove loading indicator when operations complete
 * - 7.1: Display error messages when database queries fail
 * 
 * @param {Object} props
 * @param {string} props.userId - Current user ID
 * @param {'home'|'notes'} props.view - Screen to display
 */
function MainPage({ userId, view = 'home' }) {
  const isNotesView = view === 'notes';
  usePageThemeColor(isNotesView ? null : THEME_SURFACE_COLORS);
  const currentLanguage = getLanguage();
  const { alert: showAlert, confirmDialog } = useConfirmDialog();
  // State management (Requirements 1.1, 6.3, 6.4, 7.1)
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedNotes, setHasLoadedNotes] = useState(false);
  const [notesLoadError, setNotesLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [hasFlashcards, setHasFlashcards] = useState(false);
  const [practicePoolCounts, setPracticePoolCounts] = useState(null);
  const practicePoolWarningLevel = getPracticePoolWarningLevel(practicePoolCounts);
  const [upgradeableCardCount, setUpgradeableCardCount] = useState(0);
  const [completedReviewCardCount, setCompletedReviewCardCount] = useState(0);
  const [previews, setPreviews] = useState({ levelUp: [], triumph: [] });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewRefresh, setPreviewRefresh] = useState(0);
  const [libraryPreview, setLibraryPreview] = useState({ entries: [], total: null });
  const [isLibraryPreviewLoading, setIsLibraryPreviewLoading] = useState(false);
  const [libraryPreviewError, setLibraryPreviewError] = useState(null);
  const [libraryPreviewRefresh, setLibraryPreviewRefresh] = useState(0);
  const [studyPathPreview, setStudyPathPreview] = useState(null);
  
  // Streak state (Practice Streak Calendar Requirements 1.1, 4.1)
  const [streakData, setStreakData] = useState({ streak: 0, hasPracticedToday: false });
  const [isStreakLoading, setIsStreakLoading] = useState(true);
  const [isStreakUnavailable, setIsStreakUnavailable] = useState(false);
  const streakRequestRevision = useRef(0);

  const [tournamentSummary, setTournamentSummary] = useState(null);
  const [isTournamentFeatured, setIsTournamentFeatured] = useState(false);
  const [tournamentCountdown, setTournamentCountdown] = useState('');
  const [tournamentNotice, setTournamentNotice] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showInitialManual, setShowInitialManual] = useState(
    () => !isNotesView && authService.getSession()?.pendingInitialManual === true
  );
  
  // Practice settings dialog state
  const [isPracticeSettingsOpen, setIsPracticeSettingsOpen] = useState(false);
  const [emptyPracticeSceneScope, setEmptyPracticeSceneScope] = useState(null);
  const allFlashcardsPreparationRef = useRef(null);
  const practiceLaunchKeyRef = useRef(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const streakService = new StreakService();

  // Keep the entry's DOM order current across signup/result-day boundaries.
  // This clock only updates presentation; it never fetches or starts anything.
  useEffect(() => {
    if (isNotesView) return undefined;
    let timeoutId;
    const receivedAt = Date.now();
    const serverTime = tournamentSummary?.serverTime ? new Date(tournamentSummary.serverTime).getTime() : NaN;
    const clockOrigin = Number.isFinite(serverTime) ? serverTime : receivedAt;
    const refreshPlacement = () => {
      const now = clockOrigin + Date.now() - receivedAt;
      const placement = getTournamentEntryPlacement(tournamentSummary, now);
      const countdown = getTournamentSignupCountdown(
        tournamentSummary?.currentTournament?.status === 'signup' ? tournamentSummary.currentTournament.signupClosesAt : null,
        now
      );
      setIsTournamentFeatured(placement.isFeatured);
      setTournamentCountdown(countdown.label);
      const boundaries = [placement.nextChangeAt, countdown.nextChangeAt].filter((value) => value !== null);
      if (boundaries.length) {
        timeoutId = window.setTimeout(refreshPlacement, Math.min(2147483647, Math.max(1, Math.min(...boundaries) - now)));
      }
    };
    refreshPlacement();
    return () => window.clearTimeout(timeoutId);
  }, [isNotesView, tournamentSummary]);

  // Preview text is an independent, bounded read. It never prepares a session,
  // requests TTS, or writes any progress, and must not delay the Start button.
  useEffect(() => {
    if (isNotesView) return undefined;
    let cancelled = false;
    if (!hasLoadedNotes || !hasFlashcards || (upgradeableCardCount === 0 && completedReviewCardCount === 0)) {
      setPreviews({ levelUp: [], triumph: [] });
      setPreviewError(null);
      setIsPreviewLoading(false);
      return undefined;
    }
    setIsPreviewLoading(true);
    setPreviewError(null);
    const loadPreviews = async () => {
      try {
        const data = await apiClient.getDashboardPreviews();
        if (!data || !Array.isArray(data.levelUp) || !Array.isArray(data.triumph)) {
          throw new Error('Invalid dashboard preview response');
        }
        if (!cancelled) setPreviews({ levelUp: data.levelUp, triumph: data.triumph });
      } catch (err) {
        if (!cancelled) {
          setPreviews({ levelUp: [], triumph: [] });
          setPreviewError(err);
        }
      } finally {
        if (!cancelled) setIsPreviewLoading(false);
      }
    };
    loadPreviews();
    return () => { cancelled = true; };
  }, [isNotesView, hasLoadedNotes, hasFlashcards, upgradeableCardCount, completedReviewCardCount, previewRefresh, userId]);

  useEffect(() => {
    if (isNotesView || !userId) {
      setLibraryPreview({ entries: [], total: null });
      setStudyPathPreview(null);
      setLibraryPreviewError(null);
      setIsLibraryPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLibraryPreviewLoading(true);
    setLibraryPreviewError(null);
    setLibraryPreview({ entries: [], total: null });
    setStudyPathPreview(null);
    const loadLibraryRecommendation = async () => {
      try {
        const result = await apiClient.getStudyPath(currentLanguage);
        const note = result?.next;
        if (note) {
          const details = await apiClient.getPresetLibraryItem(note.id);
          const entries = (details?.preview || []).map((card, index) => {
            const reading = card?.cardType === 'reading';
            const text = String(reading ? card.title : card.back || '').trim();
            if (!text) return null;
            return {
              id: `official:${note.id}:${index}`, source: 'official', libraryItemId: note.id,
              noteTitle: note.title, text, learningLocale: note.learningLanguage || note.learningLocale || 'en-US',
              questionText: reading ? '' : String(card.front || '').trim(),
              questionLocale: note.questionLocale || 'ko-KR',
            };
          }).filter(Boolean);
          const entry = entries.length ? entries[Math.floor(Math.random() * entries.length)] : null;
          if (entry) {
            const recommendation = { entry, title: note.title, count: result.recommendations?.length || 0 };
            if (!cancelled) {
              setStudyPathPreview(recommendation);
              setLibraryPreview({ entries, total: recommendation.count });
            }
            return;
          }
        }
        const fallback = await apiClient.getLibraryDashboardPreview(currentLanguage);
        if (!cancelled) setLibraryPreview({ entries: fallback.entries || [], total: Number(fallback.total) || 0 });
      } catch (err) {
        if (!cancelled) {
          setLibraryPreviewError(err);
          setLibraryPreview({ entries: [], total: null });
        }
      } finally {
        if (!cancelled) setIsLibraryPreviewLoading(false);
      }
    };
    loadLibraryRecommendation();
    return () => { cancelled = true; };
  }, [isNotesView, userId, currentLanguage, libraryPreviewRefresh]);
  const handleInitialManualComplete = () => {
    const session = authService.getSession();
    if (session) {
      const completedSession = { ...session };
      delete completedSession.pendingInitialManual;
      authService.setSession(completedSession);
    }
    setShowInitialManual(false);
  };

  /**
   * Component lifecycle - onMount
   * Initialize services and load notes
   * Requirements: 1.1, 6.3
   */
  useEffect(() => {
    // Initialize navigation service with navigate function
    navigationService.initialize(navigate);

    // Set up navigation error handler
    navigationService.onNavigationError((navError) => {
      console.error('Navigation error:', navError);
      setError({
        type: 'navigation',
        message: navError.message,
        details: navError.originalError?.message,
        timestamp: navError.timestamp,
        dismissible: true,
        retryable: false,
      });
    });

    // Both screens load notes; dashboard-only reads stay on home.
    loadNotes();
    if (!isNotesView) {
      loadStreakData();
      loadTournamentSummary();
      loadNotification();
    }

    // Cleanup on unmount
    return () => {
      navigationService.clearErrorHandlers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotesView]); // Routes use distinct keys to give each view a fresh lifecycle.

  /**
   * Reload data when navigating back to MainPage
   * Practice Streak Calendar Requirements: 7.4
   */
  useEffect(() => {
    if (isNotesView) return undefined;
    // Refresh streak data when component becomes visible after navigation
    // This handles the case where user returns from practice session
    const handleFocus = () => {
      loadStreakData();
      loadTournamentSummary();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotesView]);

  /**
   * Reload streak data when component becomes visible
   * Practice Streak Calendar Requirements: 7.4
   */
  useEffect(() => {
    if (isNotesView) return undefined;
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, reload streak data
        loadStreakData();
        loadTournamentSummary();
      }
    };

    const handlePracticeSessionComplete = () => {
      // Practice session completed, refresh streak data
      // Practice Streak Calendar Requirements: 2.4, 7.4
      console.log('Practice session completed, refreshing streak data');

      // Always refresh streak data
      loadStreakData();
      loadTournamentSummary();
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for practice session completion events
    window.addEventListener('practiceSessionComplete', handlePracticeSessionComplete);
    window.addEventListener(STREAK_PROTECTION_CHANGED, handlePracticeSessionComplete);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('practiceSessionComplete', handlePracticeSessionComplete);
      window.removeEventListener(STREAK_PROTECTION_CHANGED, handlePracticeSessionComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotesView]);

  useEffect(() => {
    if (isNotesView || !streakData.timezone || !streakData.today) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden && getAccountToday(streakData.timezone) !== streakData.today) loadStreakData();
    }, 60_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotesView, streakData.timezone, streakData.today]);

  /**
   * Load notes from database
   * Requirements: 1.1, 6.3, 6.4, 7.1
   */
  const loadNotes = async () => {
    try {
      setIsLoading(true);
      setNotesLoadError(null);
      setPracticePoolCounts(null);

      // Get current user ID if not provided
      const currentUserId = userId || authService.getCurrentUserId();
      
      if (!currentUserId) {
        throw new Error(t('dashboard.errors.signIn'));
      }

      const { notes: fetchedNotes, dashboard } = await databaseService.getMainPageData(currentUserId);
      setNotes(fetchedNotes);

      setHasFlashcards((dashboard?.totalCardCount || 0) > 0);
      setPracticePoolCounts(dashboard?.practiceSummaryProgress || null);
      setUpgradeableCardCount(dashboard?.upgradeableCardCount || 0);
      setCompletedReviewCardCount(dashboard?.completedCardCount || 0);
      setHasLoadedNotes(true);

    } catch (err) {
      console.error('Failed to load notes:', err);
      
      // Set error state (Requirements 7.1)
      setNotesLoadError({
        type: 'database',
        message: err.message || t('dashboard.errors.loadNotes'),
        details: err.originalError?.message,
        timestamp: new Date(),
        retryable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load streak data from backend
   * Practice Streak Calendar Requirements: 1.1, 7.3
   */
  const loadStreakData = async () => {
    const requestRevision = ++streakRequestRevision.current;
    try {
      setIsStreakLoading(true);

      // Fetch streak data from backend
      const data = await streakService.getStreakData();
      if (requestRevision !== streakRequestRevision.current) return;
      setIsStreakUnavailable(false);
      setStreakData({
        streak: data.streak || 0,
        hasPracticedToday: data.hasPracticedToday || false,
        todayPoints: data.todayPoints ?? null,
        isVacationToday: Boolean(data.enabled && data.isVacationToday),
        timezone: data.timezone,
        today: data.today,
      });

    } catch (err) {
      if (requestRevision !== streakRequestRevision.current) return;
      console.error('Failed to load streak data:', err);
      
      // A failed request is not evidence that a user's streak has reset.
      setIsStreakUnavailable(true);
    } finally {
      if (requestRevision === streakRequestRevision.current) setIsStreakLoading(false);
    }
  };

  /**
   * Load the shared world martial arts tournament summary.
   */
  const loadTournamentSummary = async () => {
    try {
      const summary = await apiClient.getTournamentSummary();
      setTournamentSummary(summary);
      setTournamentNotice(summary?.pendingChampionNotice || summary?.pendingNotice || null);
    } catch (err) {
      console.error('Failed to load tournament summary:', err);
    }
  };

  const loadNotification = async () => {
    try {
      const response = await apiClient.getCurrentNotification();
      const current = response?.notification || null;
      setNotification(current && !isNotificationDismissed(current.id) ? current : null);
    } catch (err) {
      console.error('Failed to load notification:', err);
      setNotification(null);
    }
  };

  /**
   * Handle note click - show practice settings modal for note-specific practice
   * Requirements: 2.1
   */
  const handleNoteClick = async (noteId) => {
    try {
      setError(null);
      allFlashcardsPreparationRef.current = null;

      const selectedNote = notes.find((note) => note.id === noteId);
      if (selectedNote?.isEnabled === false) {
        await showAlert({
          title: t('notes.disabledPracticeTitle'),
          message: t('notes.disabledPracticeMessage'),
          confirmLabel: t('common.ok'),
          tone: 'warning',
        });
        return;
      }

      // Get flashcards for this note
      const flashcards = await databaseService.getFlashcardsByNote(noteId);

      // Check if note has flashcards (Requirements 2.3)
      if (flashcards.length === 0) {
        setEmptyPracticeSceneScope('note');
        return;
      }

      // Store note flashcards for settings modal
      window.tempNoteFlashcards = flashcards;
      window.tempNoteId = noteId;

      // Show practice settings modal
      setIsPracticeSettingsOpen(true);

    } catch (err) {
      console.error('Failed to start drill workout:', err);
      
      setError({
        type: err.name === 'NavigationError' ? 'navigation' : 'database',
        message: err.message || t('dashboard.errors.startWorkout'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };

  /**
   * Handle start test - show practice settings modal
   * Requirements: 3.2, 3.4
   */
  const handleStartTest = useCallback(() => {
    try {
      void unlockPracticeAudio();
      setError(null);

      // Get current user ID
      const currentUserId = userId || authService.getCurrentUserId();
      
      if (!currentUserId) {
        throw new Error(t('dashboard.errors.signIn'));
      }

      delete window.tempNoteFlashcards;
      delete window.tempNoteId;

      // Open immediately. Card preparation continues while the user chooses a stage.
      setIsPracticeSettingsOpen(true);

      const preparation = databaseService.getAvailablePracticeFlashcards(currentUserId);
      allFlashcardsPreparationRef.current = preparation;
      void preparation.catch((err) => {
        console.error('Failed to prepare workout cards:', err);
      });

    } catch (err) {
      console.error('Failed to prepare workout:', err);
      
      setError({
        type: 'database',
        message: err.message || t('dashboard.errors.prepareWorkout'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  }, [userId]);

  // External training entries reuse the home launch flow, including its stage
  // choices and background card preparation. Consume the intent before leaving
  // so Back/refresh cannot reopen the choices or prepare the cards twice.
  useEffect(() => {
    if (isNotesView || location.state?.startHomePractice !== true || practiceLaunchKeyRef.current === location.key) return;
    practiceLaunchKeyRef.current = location.key;
    const { startHomePractice, ...remainingState } = location.state;
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
      replace: true,
      state: Object.keys(remainingState).length ? remainingState : null,
    });
    handleStartTest();
  }, [handleStartTest, isNotesView, location, navigate]);

  /**
   * Handle practice start with settings
   * @param {Object} settings - Practice settings
   */
  const handleStartPracticeWithSettings = async (settings) => {
    try {
      void unlockPracticeAudio();
      setIsLoading(true);
      setError(null);

      let flashcards;
      let testType = 'random';
      let noteId = null;

      // Check if this is note-specific practice
      if (window.tempNoteFlashcards) {
        flashcards = window.tempNoteFlashcards;
        noteId = window.tempNoteId;
        testType = 'note';
        // Clean up temp storage
        delete window.tempNoteFlashcards;
        delete window.tempNoteId;
      } else {
        // Get current user ID for all-cards practice
        const currentUserId = userId || authService.getCurrentUserId();
        
        if (!currentUserId) {
          throw new Error(t('dashboard.errors.signIn'));
        }

        // Reuse the request started when the settings popup opened.
        flashcards = allFlashcardsPreparationRef.current
          ? await allFlashcardsPreparationRef.current
          : await databaseService.getAvailablePracticeFlashcards(currentUserId);
        allFlashcardsPreparationRef.current = null;
      }

      // Filter out cards that should not appear; scheduler handles ordering.
      const preparedFlashcards = PracticeSettingsService.applyFilters(flashcards, settings);

      if (preparedFlashcards.length === 0) {
        setIsPracticeSettingsOpen(false);
        setIsLoading(false);
        setEmptyPracticeSceneScope(testType === 'note' ? 'note' : 'all');
        return;
      }

      // Create test context with settings
      const testContext = {
        type: testType,
        flashcards: preparedFlashcards,
        settings: settings, // Pass settings to practice session
      };

      // Add noteId if this is note-specific practice
      if (testType === 'note') {
        const inferredNoteIds = new Set(
          preparedFlashcards
            .map((card) => card.noteId)
            .filter(Boolean)
        );
        const resolvedNoteId = noteId || (inferredNoteIds.size === 1 ? [...inferredNoteIds][0] : null);

        if (resolvedNoteId) {
          testContext.noteId = resolvedNoteId;
        } else {
          testContext.type = 'random';
        }
      }

      // Close settings modal
      setIsPracticeSettingsOpen(false);

      // Navigate to test (Requirements 3.4)
      navigationService.navigateToTest(testContext);

      // Note: Streak data will be refreshed when user returns to MainPage
      // The practice session completion is handled by PracticeSession component

    } catch (err) {
      console.error('Failed to start practice:', err);
      
      setError({
        type: err.name === 'NavigationError' ? 'navigation' : 'database',
        message: err.message || t('dashboard.errors.startPractice'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
      setIsLoading(false);
    }
  };

  /**
   * Handle import note - open import interface
   * Requirements: 4.2
   */
  const handleImportNote = () => {
    try {
      setError(null);
      
      // Navigate to import page (Requirements 4.2)
      navigationService.navigateToImport();

    } catch (err) {
      console.error('Failed to navigate to import:', err);
      
      setError({
        type: 'navigation',
        message: err.message || t('dashboard.errors.openImport'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: true,
      });
    }
  };

  /**
   * Handle show list - navigate to flashcard list view
   * @param {string} noteId - Note ID
   */
  const handleShowList = (noteId) => {
    try {
      setError(null);
      
      // Navigate to map page with noteId as state
      navigationService.navigate('/map', {
        state: { noteId }
      });

    } catch (err) {
      console.error('Failed to navigate to flashcard list:', err);
      
      setError({
        type: 'navigation',
        message: err.message || t('dashboard.errors.openCards'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };

  /**
   * Clear error message
   * Requirements: 7.4
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Retry failed operation
   */
  const handleRetry = () => {
    if (error && error.retryable) {
      setError(null);
      loadNotes();
    }
  };

  const handleOpenPresetLibrary = async () => {
    try {
      const language = getLanguage();
      const response = await apiClient.getPresetLibraryLocales();
      const locales = Array.isArray(response?.locales) ? response.locales : [];
      const hasLocale = locales.some((locale) => locale.locale === language);

      if (!hasLocale) {
        await showAlert({
          title: t('presetLibrary.unavailableTitle'),
          message: t('presetLibrary.unavailableMessage'),
          confirmLabel: t('common.ok'),
          tone: 'warning',
        });
        return;
      }

      navigate(`/library?locale=${encodeURIComponent(language)}`);
    } catch (err) {
      console.error('Failed to check preset library locales:', err);
      await showAlert({
        title: t('presetLibrary.unavailableTitle'),
        message: t('presetLibrary.unavailableMessage'),
        confirmLabel: t('common.ok'),
        tone: 'warning',
      });
    }
  };

  const handleStartPracticePlayer = async (settings) => {
    try {
      setError(null);
      const profile = await apiClient.getCurrentUser();
      if (profile?.entitlements?.canUseTts !== true) {
        await showAlert({
          title: t('membership.tts.title'),
          message: t('membership.tts.message'),
          confirmLabel: t('common.ok'),
          tone: 'warning',
        });
        return;
      }

      let flashcards;
      let noteId = null;
      if (window.tempNoteFlashcards) {
        flashcards = window.tempNoteFlashcards;
        noteId = window.tempNoteId || null;
      } else {
        const currentUserId = userId || authService.getCurrentUserId();
        if (!currentUserId) throw new Error(t('dashboard.errors.signIn'));
        flashcards = allFlashcardsPreparationRef.current
          ? await allFlashcardsPreparationRef.current
          : await databaseService.getAvailablePracticeFlashcards(currentUserId);
        allFlashcardsPreparationRef.current = null;
      }

      const playlist = buildPracticePlayerPlaylist(flashcards, {
        mode: settings.playerMode,
        settings,
      });
      if (playlist.length === 0) {
        delete window.tempNoteFlashcards;
        delete window.tempNoteId;
        setIsPracticeSettingsOpen(false);
        setEmptyPracticeSceneScope(noteId ? 'note' : 'all');
        return;
      }

      delete window.tempNoteFlashcards;
      delete window.tempNoteId;
      setIsPracticeSettingsOpen(false);
      navigate('/practice-player', {
        state: {
          playerContext: {
            flashcards: playlist,
            mode: settings.playerMode,
            settings,
            noteId,
          },
        },
      });
    } catch (err) {
      console.error('Failed to start Practice Player:', err);
      setError({
        type: 'database',
        message: err.message || t('dashboard.errors.startPlayer'),
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };

  const handleTournamentClick = () => {
    try {
      navigationService.navigate('/tournament');
    } catch (err) {
      console.error('Failed to navigate to tournament:', err);

      setError({
        type: 'navigation',
        message: err.message || t('dashboard.errors.openTournament'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };

  const handleLevelUpClick = () => {
    try {
      navigationService.navigate('/level-up');
    } catch (err) {
      console.error('Failed to navigate to level-up page:', err);

      setError({
        type: 'navigation',
        message: err.message || t('dashboard.errors.openLevelUp'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };

  const handleTriumphReviewClick = (previewCard = null) => {
    if (completedReviewCardCount === 0) return;

    try {
      void unlockPracticeAudio();
      if (previewCard?.id != null) {
        navigationService.navigate('/triumph', {
          state: { highlightCardId: String(previewCard.id) },
        });
      } else {
        navigationService.navigate('/triumph');
      }
    } catch (err) {
      console.error('Failed to open triumph page:', err);

      setError({
        type: err.name === 'NavigationError' ? 'navigation' : 'database',
        message: err.message || t('dashboard.errors.openTriumph'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };

  const handleLibraryClick = (preview = null) => {
    try {
      const selectedNoteId = preview?.libraryItemId ? `&selectedNoteId=${encodeURIComponent(preview.libraryItemId)}` : '';
      navigationService.navigate(`/library?view=all&source=official&group=editor-picks&locale=${encodeURIComponent(currentLanguage)}${selectedNoteId}`);
    } catch (err) {
      console.error('Failed to open Library:', err);
      setError({
        type: 'navigation',
        message: err.message || t('dashboard.errors.openLibrary'),
        details: err.originalError?.message,
        timestamp: new Date(),
        dismissible: true,
        retryable: false,
      });
    }
  };
  const tournament = tournamentSummary?.currentTournament;
  const humanCount = (tournament?.participants || []).filter((participant) => participant.type === 'human').length;
  const hasTournamentApplication = Boolean(tournamentSummary?.userApplication);
  const tournamentFooter = tournament?.status === 'signup'
    ? t(hasTournamentApplication && !tournamentSummary?.userEntry ? 'tournamentUx.applicationPending' : 'dashboard.tournament.signupOpen')
    : tournament?.status === 'active'
      ? (localizeTournamentLabel(tournament.currentPhaseLabel) || t('dashboard.tournament.matchDay'))
      : t('dashboard.tournament.nextArena');
  const tournamentCardClassName = [
    'home-tournament-entry',
    isTournamentFeatured ? 'home-tournament-entry-featured' : '',
  ].filter(Boolean).join(' ');

  const formatTournamentDate = (value) => new Date(value).toLocaleDateString(
    getLanguage() === 'ko' ? 'ko-KR' : 'en-US',
    { month: 'short', day: 'numeric', timeZone: 'Asia/Seoul' }
  );

  const getTournamentCardValue = () => {
    if (!tournament) return t('dashboard.tournament.loading');
    if (tournament.status === 'signup') {
      if (tournamentSummary?.userEntry) {
        return t('dashboard.tournament.signedUp', undefined, { count: humanCount });
      }
      const deadline = t('dashboard.tournament.signupCloses', undefined, {
        date: formatTournamentDate(tournament.signupClosesAt),
      });
      return tournamentCountdown ? `${deadline} · ${tournamentCountdown}` : deadline;
    }
    if (tournament.status === 'active') {
      const match = tournamentSummary?.userTodayMatch;
      return match ? t('dashboard.tournament.todayMatch', undefined, {
        home: match.home?.name || t('dashboard.tournament.fighter'),
        away: match.away?.name || t('dashboard.tournament.fighter'),
      }) : t('dashboard.tournament.inProgress', undefined, {
        phase: localizeTournamentLabel(tournament.currentPhaseLabel) || t('dashboard.tournament.title'),
      });
    }
    if (tournament.status === 'cancelled') {
      return t('dashboard.tournament.cancelled');
    }
    if (tournamentSummary?.userLastResult) {
      return t('dashboard.tournament.lastResult', undefined, {
        rank: tournamentSummary.userLastResult.rank,
        result: localizeTournamentLabel(tournamentSummary.userLastResult.resultLabel) || '',
      });
    }
    return t('dashboard.tournament.next', undefined, {
      date: formatTournamentDate(tournament.startAt),
      signupDate: formatTournamentDate(tournament.signupOpensAt),
    });
  };

  const handleTournamentNoticeClose = async () => {
    const matchId = tournamentNotice?.matchId;
    setTournamentNotice(null);
    if (!matchId) return;

    try {
      await apiClient.markTournamentNoticeSeen(matchId);
      loadTournamentSummary();
    } catch (err) {
      console.error('Failed to mark tournament notice seen:', err);
    }
  };

  const handleNotificationClose = (dontShowAgain) => {
    if (dontShowAgain && notification?.id) {
      dismissNotification(notification.id);
    }
    setNotification(null);
  };

  const tournamentEntry = (
    <button
      key="home-tournament-entry"
      type="button"
      className={tournamentCardClassName}
      onClick={handleTournamentClick}
      title={t('dashboard.tournament.open')}
    >
      <img src={worldTournamentCupImage} className="home-tournament-icon" alt="" aria-hidden="true" width={32} height={32} />
      <span className="home-tournament-title">{t('dashboard.tournament.title')}</span>
      <span className="home-tournament-status">{getTournamentCardValue()}</span>
      {tournament && <span className="home-tournament-phase">{tournamentFooter}</span>}
      <ArrowRight className="home-tournament-arrow" size={20} aria-hidden="true" />
    </button>
  );

  return (
    <div className={`main-page ${isNotesView ? 'notes-page' : 'main-page-home'}`}>
      <div className="main-page-content">
        {isNotesView && (
          <header className="notes-page-header app-page-header">
            <div className="notes-page-heading-copy app-page-header-copy">
              <h1>{t('interface.navigation.notes')}</h1>
              {practicePoolWarningLevel > 0
                ? <PracticePoolRecommendation level={practicePoolWarningLevel} />
                : <p>{t('dashboard.notes.pageSubtitle')}</p>}
            </div>
            <div className="notes-page-header-actions app-page-header-actions">
              <ImportButton
                onClick={handleImportNote}
                iconOnly
                label={t('dashboard.newNoteShort')}
                ariaLabel={t('dashboard.newNoteShort')}
              />
            </div>
          </header>
        )}
        {isNotesView && <NoteImportNotice />}
        {notesLoadError && (
          <ErrorMessage
            error={notesLoadError}
            onRetry={loadNotes}
            type={notesLoadError.type}
          />
        )}
        {/* Error display (Requirements 7.1, 7.4) */}
        {error && (
          <ErrorMessage
            error={error}
            onDismiss={clearError}
            onRetry={error.retryable ? handleRetry : undefined}
            type={error.type}
          />
        )}

        {/* The trainer motivates; only the explicit button starts a workout. */}
        {!isNotesView && (hasFlashcards || practicePoolWarningLevel > 0) && (
          <div className="home-training-hub">
            {hasFlashcards && (
              <TrainingHero
                streak={streakData.streak}
                hasPracticedToday={streakData.hasPracticedToday}
                isVacationToday={streakData.isVacationToday}
                todayPoints={streakData.todayPoints}
                isLoading={isStreakLoading}
                unavailable={isStreakUnavailable}
                isStarting={isLoading}
                onStart={handleStartTest}
              />
            )}
            {hasFlashcards && isTournamentFeatured && tournamentEntry}
            {(hasFlashcards || practicePoolWarningLevel > 0) && (
              <HomeLearningPreviews writingPractice
                afterWritingContent={<VocabularyHomeEntry userId={userId} />}
                trailingContent={hasFlashcards && !isTournamentFeatured ? tournamentEntry : null}
                levelUp={previews.levelUp}
                triumph={previews.triumph}
                library={libraryPreview.entries}
                libraryRecommendationEntry={studyPathPreview?.entry || null}
                libraryRecommendationTitle={studyPathPreview?.title || ''}
                libraryRecommendationCount={studyPathPreview?.count || 0}
                libraryRecommendationLabel={currentLanguage === 'ko' ? '다음 추천 노트' : 'Next recommended note'}
                upgradeableCount={upgradeableCardCount}
                completedCount={completedReviewCardCount}
                libraryCount={libraryPreview.total}
                isLoading={isPreviewLoading}
                error={previewError}
                isLibraryLoading={isLibraryPreviewLoading}
                libraryError={libraryPreviewError}
                practicePoolWarningLevel={practicePoolWarningLevel}
                libraryOnly={!hasFlashcards}
                onRetry={() => {
                  setPreviewRefresh((value) => value + 1);
                  setLibraryPreviewRefresh((value) => value + 1);
                }}
                onLibraryRetry={() => setLibraryPreviewRefresh((value) => value + 1)}
                onLevelUp={handleLevelUpClick}
                onTriumph={handleTriumphReviewClick}
                onLibrary={handleLibraryClick}
                paused={Boolean(isPracticeSettingsOpen || tournamentNotice || notification || showInitialManual || emptyPracticeSceneScope)}
              />
            )}
          </div>
        )}

        {!isNotesView && !hasFlashcards && (hasLoadedNotes || notesLoadError) && <VocabularyHomeEntry userId={userId} />}

        {!isNotesView && isLoading && !hasLoadedNotes && (
          <div className="home-loading-state" role="status" aria-live="polite">
            {t('dashboard.notes.loading')}
          </div>
        )}

        {!isNotesView && hasLoadedNotes && !isLoading && !notesLoadError && !hasFlashcards && (
          <section className="home-empty-state" aria-labelledby="home-empty-title">
            <h2 id="home-empty-title">{t('dashboard.homeEmpty.title')}</h2>
            <p>{t('dashboard.homeEmpty.message')}</p>
            <div className="home-empty-actions" role="group" aria-label={t('dashboard.noteActions')}>
              <ImportButton onClick={handleImportNote} />
              <button type="button" className="btn-preset" onClick={handleOpenPresetLibrary}
                aria-label={t('dashboard.browseLibrary')}>
                {t('dashboard.library')}
              </button>
              <button type="button" className="home-empty-notes" onClick={() => navigate('/notes')}>
                {t('interface.navigation.notes')}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          </section>
        )}

        {/* Note List (Requirements 1.1, 1.2, 1.4) */}
        {/* Loading indicator is handled by NoteList component (Requirements 6.3, 6.4) */}
        {isNotesView && (isLoading || (hasLoadedNotes && (!notesLoadError || notes.length > 0))) && (
          <NoteList
            notes={notes}
            onNoteClick={handleShowList}
            onNotePractice={handleNoteClick}
            isLoading={isLoading && (!hasLoadedNotes || notes.length === 0)}
          />
        )}


      </div>

      {/* Practice Settings Dialog */}
      <PracticeSettings
        isOpen={isPracticeSettingsOpen}
        onClose={() => {
          allFlashcardsPreparationRef.current = null;
          delete window.tempNoteFlashcards;
          delete window.tempNoteId;
          setIsPracticeSettingsOpen(false);
        }}
        onStart={handleStartPracticeWithSettings}
        onStartPlayer={handleStartPracticePlayer}
      />

      {!isNotesView && (
        <>
          <TournamentResultPopup
            notice={tournamentNotice}
            onClose={handleTournamentNoticeClose}
          />
          <NotificationPopup
            notification={tournamentNotice ? null : notification}
            onClose={handleNotificationClose}
          />
        </>
      )}

      {emptyPracticeSceneScope && (
        <NoCardsPracticeScene
          scope={emptyPracticeSceneScope}
          onContinue={() => setEmptyPracticeSceneScope(null)}
        />
      )}

      {!isNotesView && showInitialManual && (
        <InitialManualScene onContinue={handleInitialManualComplete} />
      )}

      {confirmDialog}

    </div>
  );
}

export default MainPage;
